import {useEffect, useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {toast} from 'sonner';
import {API} from '@chekin/api';
import {QUERY_CACHE_KEYS, LocationType, SelectOption} from '@chekin/core';

function getLocationsAsOptions(locations: Array<LocationType>): SelectOption[] {
  if (!locations) {
    return [];
  }

  return locations.map(l => {
    return {
      label: l?.country?.name,
      value: l?.country?.code,
    };
  });
}

type UseLocationsOptions = {
  enabled?: boolean;
};

function useFetchLocations(props?: UseLocationsOptions) {
  const {i18n} = useTranslation();

  const {
    data: locations,
    isError,
    error,
    isLoading: isLoadingLocations,
  } = useQuery({
    queryKey: [QUERY_CACHE_KEYS.locations, i18n.language],
    queryFn: () => API.locations.getAllCountries(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...props,
  });

  useEffect(() => {
    if (error) {
      toast.error('Requested counties list could not be found. Please contact support.');
    }
  }, [error]);

  const locationsAsOptions = useMemo(() => {
    if (locations) {
      return getLocationsAsOptions(locations);
    }

    return [];
  }, [locations]);

  const phoneCodesAsOptions = useMemo(() => {
    if (locations) {
      return locations?.map(l => {
        const phoneCodeValue = `+${l.phone_code}`;

        return {
          value: phoneCodeValue,
          label: `${l?.country?.name} (${phoneCodeValue})`,
        };
      });
    }

    return [];
  }, [locations]);

  return {
    locations,
    isError,
    isLoadingLocations,
    error,
    locationsAsOptions,
    phoneCodesAsOptions,
  };
}

export {useFetchLocations};
